import { ArrowLeft, Clock, FileText, Tag, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

interface InnoventureTestHeaderProps {
  title: string;
  jobTitle?: string;
  invitationOnly: boolean;
  questionCount: number;
  timeLimit?: number;
  timeLimitType?: string;
  totalTimeLimit?: number;
  totalQuestionTimeSeconds?: number;
  scheduledStartTime?: string;
  scheduledEndTime?: string;
  onBack: () => void;
  accentColor: string;
  gradientStart: string;
  gradientEnd: string;
  logo?: string;
}

export function InnoventureTestHeader({
  title,
  jobTitle,
  invitationOnly,
  questionCount,
  timeLimit,
  timeLimitType,
  totalTimeLimit,
  totalQuestionTimeSeconds,
  scheduledStartTime,
  scheduledEndTime,
  onBack,
  accentColor,
  gradientStart,
  gradientEnd,
  logo,
}: InnoventureTestHeaderProps) {
  const getTimeLimit = () => {
    // Primary: Use sum of individual question times (source of truth)
    if (totalQuestionTimeSeconds && totalQuestionTimeSeconds > 0) {
      const totalMinutes = Math.ceil(totalQuestionTimeSeconds / 60);
      return `${totalMinutes} minute${totalMinutes !== 1 ? 's' : ''} total`;
    }
    // Fallback: Use total time limit if specified
    else if (timeLimitType === 'total' && totalTimeLimit) {
      return `${totalTimeLimit} minute${totalTimeLimit !== 1 ? 's' : ''} total`;
    }
    return 'No time limit';
  };

  return (
    <div 
      className="relative overflow-hidden rounded-t-lg p-8 bg-purple-50"
    >
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="text-gray-700 hover:bg-gray-700/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Tests
          </Button>
          <Badge 
            variant={invitationOnly ? 'default' : 'secondary'}
            className="shrink-0"
            style={invitationOnly ? { backgroundColor: accentColor } : undefined}
          >
            {invitationOnly ? 'Invitation Only' : 'Public'}
          </Badge>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div 
            className="flex items-center justify-center w-12 h-12 rounded-lg bg-gray-700/10 backdrop-blur-sm overflow-hidden"
          >
            {logo ? (
              <img src={logo} alt="Innoventure" className="w-full h-full object-contain p-1" />
            ) : (
              <span className="text-gray-900 font-bold text-lg">I+</span>
            )}
          </div>
          <div>
            <h3 className="text-gray-700 text-sm font-medium">Innoventure</h3>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-3">{title}</h1>
        
        {jobTitle && (
          <div className="flex items-center gap-2 text-gray-700 mb-4">
            <Tag className="h-4 w-4" />
            <span className="font-medium">{jobTitle}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-4 text-gray-700 text-sm">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-gray-700/10">
              <FileText className="h-4 w-4" />
            </div>
            <span className="font-medium">{questionCount} questions</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-gray-700/10">
              <Clock className="h-4 w-4" />
            </div>
            <span className="font-medium">{getTimeLimit()}</span>
          </div>

          {scheduledStartTime && (
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-gray-700/10">
                <Calendar className="h-4 w-4" />
              </div>
              <span className="font-medium">
                Starts: {format(new Date(scheduledStartTime), 'MMM d, yyyy h:mm a')}
              </span>
            </div>
          )}

          {scheduledEndTime && (
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-md bg-gray-700/10">
                <Calendar className="h-4 w-4" />
              </div>
              <span className="font-medium">
                Ends: {format(new Date(scheduledEndTime), 'MMM d, yyyy h:mm a')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
